/**
 * Наборы интерфейса на сервере (бэкенд: api/ui_prefs.py).
 *
 * Наборы сканера и фандинга и графики спредов раньше жили только в
 * localStorage — очистка данных сайта в браузере (29.09) стёрла их целиком.
 * Теперь localStorage — кэш: после входа забираем значения с сервера и кладём
 * в него, а каждое изменение отправляем обратно (с задержкой, пачкой).
 *
 * Страницы читают localStorage при монтировании, как и раньше. Если сервер
 * ответил уже после этого, шлём событие PREFS_EVENT с именами изменившихся
 * ключей — страница перечитывает своё состояние.
 */
import { apiClient } from '../api/client';

export const SERVER_PREF_KEYS = [
  'sb_scanner_presets',
  'sb_scanner_cols',
  'sb_funding_presets',
  'spreadbot.spreadCharts.v1',
] as const;
export type ServerPrefKey = typeof SERVER_PREF_KEYS[number];
export const PREFS_EVENT = 'sb-server-prefs';

let synced = false;
const lastServer: Partial<Record<ServerPrefKey, string>> = {};
const timers: Partial<Record<ServerPrefKey, number>> = {};

/** Пустое ли значение: пустые наборы не выгружаем поверх серверных. */
const isEmpty = (raw: string | null): boolean => {
  if (!raw) return true;
  try {
    const v = JSON.parse(raw);
    if (Array.isArray(v)) return v.length === 0;
    if (v && typeof v === 'object') {
      if (Array.isArray((v as { items?: unknown[] }).items)) return (v as { items: unknown[] }).items.length === 0;
      return Object.keys(v).length === 0;
    }
    return false;
  } catch { return true; }
};

const pushNow = async (key: ServerPrefKey, raw: string) => {
  try {
    await apiClient.putUiPref(key, JSON.parse(raw));
    lastServer[key] = raw;
  } catch { /* сеть/сервер — следующее изменение отправит снова */ }
};

/** Раз после входа: сервер → localStorage; чего на сервере нет — выгрузить. */
export async function syncServerPrefs(): Promise<void> {
  let server: Record<string, { value: unknown }>;
  try { server = await apiClient.getUiPrefs(); } catch { return; }
  const changed: ServerPrefKey[] = [];
  for (const key of SERVER_PREF_KEYS) {
    let local: string | null = null;
    try { local = localStorage.getItem(key); } catch { /* */ }
    const s = server[key];
    if (s && 'value' in s) {
      const raw = JSON.stringify(s.value);
      lastServer[key] = raw;
      if (raw !== local) {
        try { localStorage.setItem(key, raw); changed.push(key); } catch { /* quota */ }
      }
    } else if (!isEmpty(local)) {
      // Первый вход после выката: наборы есть только в этом браузере.
      await pushNow(key, local as string);
    }
  }
  synced = true;
  if (changed.length) window.dispatchEvent(new CustomEvent(PREFS_EVENT, { detail: changed }));
}

/** Страница сохранила значение в localStorage — отправить его на сервер. */
export function pushServerPref(key: ServerPrefKey, raw: string): void {
  // До синхронизации не шлём: страница при монтировании пишет своё начальное
  // (часто пустое) состояние, и оно затёрло бы наборы на сервере.
  if (!synced || raw === lastServer[key]) return;
  window.clearTimeout(timers[key]);
  timers[key] = window.setTimeout(() => { void pushNow(key, raw); }, 1000);
}

/** Подписка на «сервер прислал новое» для одного ключа. */
export function onServerPref(key: ServerPrefKey, cb: () => void): () => void {
  const h = (e: Event) => {
    const keys = (e as CustomEvent<string[]>).detail || [];
    if (keys.includes(key)) cb();
  };
  window.addEventListener(PREFS_EVENT, h);
  return () => window.removeEventListener(PREFS_EVENT, h);
}
